import InvalidRequestDataException from "@/errors/InvalidRequestDataException";
import TokenExpiredException from "@/errors/TokenExpiredException";
import TokenInvalidException from "@/errors/TokenInvalidException";
import UnauthorizedError from "@/errors/UnauthorizedError";
import InvalidBusinessProfileRequestedError from "@/errors/InvalidBusinessProfileRequestedError";
import errorCodes from "@/constants/errorCodes";

export enum EmbedMessageType {
  UserMessage = "userMessage",
  Error = "error",
  AppReady = "appReady",
  GoBackFromEmbedMode = "goBackFromEmbedMode",
  GetValidEmbedToken = "getValidEmbedToken", // keep until fully removed
  RequestSessionToken = "REQUEST_SESSION_TOKEN",
  SessionToken = "SESSION_TOKEN",
  SessionTokenError = "SESSION_TOKEN_ERROR",
  ForceLogout = "FORCE_LOGOUT",
  SessionRevoked = "SESSION_REVOKED",
}

export enum EmbedMessageSubType {
  Success = "success",
  Error = "error",
  Warning = "warning",
}

export type EmbedMessagePayload = {
  type: EmbedMessageType;
  subtype?: EmbedMessageSubType;
  message?: string;
  errorName?: string;
  errorCode?: number;
};

export function notifyParent(payload: EmbedMessagePayload, overrideOrigin?: string) {
  if (window.top == null) {
    throw new Error("No parent window found to notify")
  }
  const defaultOrigin = window.location.pathname.includes("grab-engine")
    ? import.meta.env.VITE_CREATIVE_BUILDER_URL
    : import.meta.env.VITE_VARIANT_BUILDER_URL;

  let targetOrigin = overrideOrigin || defaultOrigin || "*";
  if (targetOrigin !== "*" && targetOrigin.endsWith("/")) {
    targetOrigin = targetOrigin.slice(0, -1);
  }
  window.top.postMessage(payload, targetOrigin);
}

export function mapColorToSubType(color: string): EmbedMessageSubType {
  switch (color) {
    case "success":
    case "green":
      return EmbedMessageSubType.Success;

    case "error":
    case "red":
      return EmbedMessageSubType.Error;

    case "warning":
    case "orange":
    default:
      return EmbedMessageSubType.Warning;
  }
}

let isSessionRevoked = false;

export function getIsSessionRevoked(): boolean {
  return isSessionRevoked;
}

export function setSessionRevoked(val: boolean = true): void {
  isSessionRevoked = val;
}

export function resetSessionRevokedState(): void {
  isSessionRevoked = false;
}

export function isTokenVersionRevoked(err: any): boolean {
  if (!err) return false;
  const original = err?.originalError || err;
  const responseData = original?.response?.data || err?.response?.data || err;
  return Boolean(
    responseData?.errorCode === 16 ||
    original?.errorCode === 16 ||
    err?.errorCode === 16 ||
    (typeof responseData?.message === 'string' && responseData.message.toLowerCase().includes('token version')) ||
    (typeof responseData?.error_description === 'string' && responseData.error_description.toLowerCase().includes('token version')) ||
    (typeof original?.message === 'string' && original.message.toLowerCase().includes('token version')) ||
    (typeof err?.message === 'string' && err.message.toLowerCase().includes('token version'))
  );
}

export const handleEmbedFlowError = (e: any) => {
  window.onbeforeunload = null
  let errorName = 'UnknownError'
  let errorCode = 500

  const original = e?.originalError || e
  const responseData = original?.response?.data || e?.response?.data || e

  if (isTokenVersionRevoked(e)) {
    isSessionRevoked = true
    notifyParent({
      type: EmbedMessageType.SessionRevoked,
      errorName: 'SessionRevokedException',
      errorCode: 401,
      message: responseData?.message || responseData?.error_description || original?.message || e?.message || 'The user token version is not valid.',
    })
    return
  }

  if (e instanceof InvalidRequestDataException) {
    errorName = InvalidRequestDataException.name
    errorCode = errorCodes.InvalidRequestDataException
  } else if (e instanceof TokenExpiredException) {
    errorName = TokenExpiredException.name
    errorCode = errorCodes.TokenExpiredException
  } else if (e instanceof TokenInvalidException) {
    errorName = TokenInvalidException.name
    errorCode = errorCodes.TokenInvalidException
  } else if (e instanceof UnauthorizedError || e?.response?.status === 401) {
    errorName = UnauthorizedError.name
    errorCode = errorCodes.Unauthorized
  } else if (e instanceof InvalidBusinessProfileRequestedError) {
    errorName = InvalidBusinessProfileRequestedError.name
    errorCode = errorCodes.InvalidRequestDataException
  }

  notifyParent({
    errorName,
    errorCode,
    message: e?.message || responseData?.message || 'Unknown error occurred',
    type: EmbedMessageType.Error,
  })
}

export function requestSessionTokenFromParent(): Promise<string> {
  if (isSessionRevoked) {
    return Promise.reject(new Error('Session has been revoked; cannot request session token'));
  }

  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      window.removeEventListener('message', onMessage);
      reject(new Error('Timed out waiting for SESSION_TOKEN from VB'));
    }, 15_000);

    const onMessage = (event: MessageEvent) => {
      if (event.data?.type === EmbedMessageType.SessionToken) {
        clearTimeout(timeout);
        window.removeEventListener('message', onMessage);
        resolve(event.data.token);
      }
      if (event.data?.type === EmbedMessageType.SessionTokenError) {
        clearTimeout(timeout);
        window.removeEventListener('message', onMessage);
        reject(new Error('VB failed to provide a session token'));
      }
    };

    window.addEventListener('message', onMessage);
    const overrideOrigin = window.location.pathname.includes("grab-engine")
      ? import.meta.env.VITE_CREATIVE_BUILDER_URL
      : undefined;

    notifyParent({ 
      type: EmbedMessageType.RequestSessionToken,
      clientAppId: import.meta.env.VITE_EMBED_CLIENT_APP_ID,
      consumerDomain: window.location.hostname
    } as any, overrideOrigin);
  });
}
